import { Card } from "@material-tailwind/react";
import { CiMenuKebab } from "react-icons/ci";
import { useEffect, useState } from "react";
import { api } from "../../api";

interface User {
  id: number;
  username: string;
  email: string;
  disabled: boolean;
  role: string;
  favourites: {
    carbon_projects: string[];
    mangrove_projects: string[];
  };
}

const UserManagement = () => {
  const [emailOptions, setEmailOptions] = useState<Array<{ mail: string; displayName: string }>>(
    [],
  );

  const fetchEmailOptions = async () => {
    const response = await api.get("/users/to-create");
    setEmailOptions(response.data);
  };

  useEffect(() => {
    fetchEmailOptions();
  }, []);
  const [users, setUsers] = useState<User[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  setCurrentPage;
  const [pageSize] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [activeMenu, setActiveMenu] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    role: "GENERAL",
    password: "",
  });

  const TABLE_HEAD = ["Username", "Email", "Role", "Status", "Actions"];

  const fetchUsers = async () => {
    const {
      data: { data },
    }: any = await api.get(
      `/users/list?page=${currentPage}&page_size=${pageSize}&search=${searchQuery}`,
    );
    setUsers(data);
  };

  const handleCreateUser = async () => {
    await api.post("/users/", formData);
    setIsModalOpen(false);
    fetchUsers();
    resetForm();
  };

  const handleUpdateUser = async () => {
    if (selectedUser) {
      await api.put(`/users/${selectedUser.id}`, formData);
      setIsModalOpen(false);
      fetchUsers();
      resetForm();
    }
  };

  const handleDeleteUser = async (userId: number) => {
    if (confirm("Are you sure you want to delete this user?")) {
      await api.delete(`/users/${userId}`);
      fetchUsers();
    }
  };

  const resetForm = () => {
    setFormData({
      username: "",
      email: "",
      role: "GENERAL",
      password: "",
    });
    setSelectedUser(null);
  };

  useEffect(() => {
    fetchUsers();
  }, [currentPage]);
  // Add this to your existing state declarations
  const [searchQuery, setSearchQuery] = useState("");

  // Add useEffect to trigger search when query changes
  useEffect(() => {
    fetchUsers();
  }, [currentPage, searchQuery]);
  return (
    <section className="space-y-4 px-4">
      <div
        style={{ maxWidth: `calc(100vw - 336px)` }}
        className="rounded-md bg-white p-4 shadow-sm"
      >
        <div className="flex items-center justify-between">
          <div className="mb-4 max-w-[400px]">
            <input
              type="text"
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-[#714ECE] focus:outline-none"
            />
          </div>
          <button
            onClick={() => {
              resetForm();
              setIsModalOpen(true);
            }}
            className="rounded-md bg-[#714ECE] px-4 py-2 text-white"
          >
            Add User
          </button>
        </div>

        <Card placeholder={""} className="h-full w-full overflow-auto">
          <table className="w-full min-w-max table-auto text-left">
            <thead>
              <tr>
                {TABLE_HEAD.map((head) => (
                  <th key={head} className="border-b border-blue-gray-100 bg-blue-gray-50 p-4">
                    <span className="text-sm font-medium text-gray-700">{head}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="text-sm">
              {users.map((user, index) => {
                const isLast = index === users.length - 1;
                const classes = isLast ? "p-4" : "p-4 border-b border-blue-gray-50";

                return (
                  <tr key={user.id}>
                    <td className={classes}>
                      <span className="text-[#714ECE]">{user.username || "—"}</span>
                    </td>
                    <td className={classes}>
                      <span>{user.email}</span>
                    </td>
                    <td className={classes}>
                      <span>{user.role}</span>
                    </td>
                    <td className={classes}>
                      <span>{user.disabled ? "Disabled" : "Active"}</span>
                    </td>
                    <td className={classes}>
                      <div className="relative">
                        <button
                          onClick={() => setActiveMenu(activeMenu === user.id ? null : user.id)}
                        >
                          <CiMenuKebab className="cursor-pointer" />
                        </button>
                        {activeMenu === user.id && (
                          <div className="absolute right-0 z-10 mt-2 w-56 rounded-md bg-white shadow-lg">
                            <button
                              className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                              onClick={() => {
                                setSelectedUser(user);
                                setFormData({
                                  username: user.username,
                                  email: user.email,
                                  role: user.role,
                                  password: "",
                                });
                                setIsModalOpen(true);
                                setActiveMenu(null);
                              }}
                            >
                              Edit
                            </button>
                            <button
                              className="block w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-gray-100"
                              onClick={() => {
                                handleDeleteUser(user.id);
                                setActiveMenu(null);
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      </div>

      {isModalOpen && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 "
          style={{ zIndex: 4000, margin: 0 }}
        >
          <div className="w-full max-w-sm rounded-lg bg-white p-6">
            <h2 className="mb-4 text-lg font-medium">
              {selectedUser ? "Edit User" : "Add New User"}
            </h2>
            <form className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Username</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-[#714ECE] focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <select
                  value={formData.email}
                  onChange={(e) => {
                    const selected = emailOptions.find((opt) => opt.mail === e.target.value);
                    setFormData({
                      ...formData,
                      email: e.target.value,
                      username: selected?.displayName || "",
                    });
                  }}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-[#714ECE] focus:outline-none"
                >
                  <option value="">Select an email</option>
                  {emailOptions.map((option) => (
                    <option key={option.mail} value={option.mail}>
                      {option.displayName} ({option.mail})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Role</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-[#714ECE] focus:outline-none"
                >
                  <option value="GENERAL">General</option>
                  <option value="ADMIN">Admin</option>
                  <option value="ADMIN_APPROVER">Admin Approver</option>
                </select>
              </div>
              {/* {!selectedUser && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Password</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-[#714ECE] focus:outline-none"
                  />
                </div>
              )} */}
              <div className="mt-4 flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="rounded-md border px-4 py-2 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={selectedUser ? handleUpdateUser : handleCreateUser}
                  className="rounded-md bg-[#714ECE] px-4 py-2 text-white hover:bg-[#5f41ad]"
                >
                  {selectedUser ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
};

export default UserManagement;
